import { PerfilService } from './../api/services/perfil.service';
import { EncryptionService } from './../api/services/encryption.service';
import { Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { UserDto } from 'src/app/api/models';
import { AuthService } from 'src/app/api/services/auth.service';
import { SnackBarService } from '../api/services/snack-bar.service';
import { CookieService } from 'ngx-cookie-service';
import { ComunicadosService } from '../api/services';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StorageKeys } from '../api/models/storageKeys';
import { ProfileDataRequest } from '../api/models/perfil';
import { PortalVersionService } from '../api/services/version-check.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  subscription: Subscription[] = [];
  showPassword: boolean = false;
  isLoading: boolean = false;
  showTokensModal = false;
  private pendingUser: UserDto | null = null;
  private skipTokensCheck = false;
  usernameForTokens: string | undefined;

  constructor(
    private authService: AuthService,
    private router: Router,
    private snackBarService: SnackBarService,
    private cookieService: CookieService,
    private comunicadoServ: ComunicadosService,
    private formBuilder: FormBuilder,
    private encryptionService: EncryptionService,
    private encrypt: EncryptionService,
    private PerfilService: PerfilService,
    private portalVersionService: PortalVersionService,
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    // Check version first - will reload page if mismatch detected
    const versionOk = await this.portalVersionService.checkAndReloadIfNeeded();

    // If reload triggered, code below won't execute
    if (!versionOk) {
      return;
    }

    const bio2Token = localStorage.getItem('BIO2_AccessToken');
    //this.authService.clearSessionStorage()
    if (bio2Token) {
      const decodedToken = this.authService.getDecodedAccessToken(bio2Token);
      const role = decodedToken['role'];
      if (role == 'Administrador') {
        this.router.navigate(['/login/selection']);
      }
      if (role == 'Laboratorio') {
        this.router.navigateByUrl('/home');
        //this.router.navigate(['login/selection/subLogin']);
      } else {
        this.router.navigateByUrl('/home');
      }
    }
  }

  async login(): Promise<void> {
    const usernameValue = this.loginForm.value.username?.trim() || '';
    const passwordValue = this.loginForm.value.password?.trim() || '';

    // Unencrypted username for token checks
    this.usernameForTokens = usernameValue;

    const user: UserDto = {
      username: this.encryptionService.encrypt(usernameValue),
      password: this.encryptionService.encrypt(passwordValue),
    };

    // If already re-logging after confirmation, skip tokens check
    if (this.skipTokensCheck) {
      this.skipTokensCheck = false;
      return this.executeLogin(user);
    }

    this.isLoading = true;

    // Check if the user is a lab (first letter is l)
    if (usernameValue && usernameValue.charAt(0).toLowerCase() === 'l') {
      this.subscription.push(
        this.authService.userHasTokens(usernameValue).subscribe({
          next: (hasTokens) => {
            if (hasTokens) {
              // Stop loader and show modal
              this.isLoading = false;
              this.pendingUser = user;
              this.showTokensModal = true;
              return;
            }

            // No tokens detected so continue with normal login
            this.executeLogin(user);
          },
          error: (err) => {
            console.error('Error checking user tokens', err);
            this.isLoading = false;
            this.snackBarService.openSnackBar(
              'Não foi possivel verificar as sessões atuais do utilizador. Por favor tente novamente.',
              '#F97066',
              '../../assets/Icons/ErrorIcon.png'
            );
          },
        })
      );
    } else {
      // Not a lab so continue with normal login
      this.executeLogin(user);
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  confirmContinueLogin(): void {
    this.showTokensModal = false;
    this.skipTokensCheck = true;

    const user = this.pendingUser;

    if (user && this.usernameForTokens) {
      this.subscription.push(
        this.authService.userTokensDeleted(this.usernameForTokens).subscribe({
          next: (tokensDeleted) => {
            if (tokensDeleted) {
              this.executeLogin(user);
              this.pendingUser = null;
            }
          },
          error: (err) => {
            console.error('Error deleting user tokens', err);
            this.isLoading = false;
            this.snackBarService.openSnackBar(
              'Não foi possivel terminar as sessões atuais do utilizador. Por favor tente novamente.',
              '#F97066',
              '../../assets/Icons/ErrorIcon.png'
            );
          },
        })
      );
    }
  }

  cancelLogin(): void {
    this.showTokensModal = false;
    this.pendingUser = null;
    this.isLoading = false;

    this.snackBarService.openSnackBar(
      'Login cancelado.',
      '#F97066',
      '../../assets/Icons/ErrorIcon.png'
    );
  }

  private executeLogin(user: UserDto): void {
    this.isLoading = true;

    this.subscription.push(
      this.authService.login(user).subscribe(
        async (resp) => {
          localStorage.setItem('BIO2_AccessToken', resp.biO2_AccessToken);
          localStorage.setItem('BIO2_RefreshToken', resp.biO2_RefreshToken);
          this.authService.decodeToken(resp.biO2_AccessToken);

          const decodedToken = this.authService.getDecodedAccessToken(resp.biO2_AccessToken);
          const role = decodedToken['role'];

          this.encrypt.setEncryptedItem(StorageKeys.BIO2_CLIENT_ADDRESS_ID, decodedToken['morada']);

          const activeComunicado = await this.comunicadoServ.getComunicadoByUser(decodedToken['unique_name']);
          this.comunicadoServ.setActiveComunicado(activeComunicado);

          this.isLoading = false;
          this.PerfilService.getProfileData();


          if (!this.cookieService.check('cookiesAcceptedTC')) {
            this.router.navigate(['/condicoes']);
          } else if (!decodedToken['avatar'] || decodedToken['avatar'].toLowerCase() === 'null') {
            this.router.navigate(['/avatar']);
          } else if (activeComunicado.length > 0) {
            this.router.navigate(['/comunicado']);
          } else if (role === 'Administrador') {
            this.router.navigate(['/login/selection']);
          } else if (role === 'Laboratorio' || role === 'SuperClient') {
            this.router.navigate(['login/selection/subLogin']);
          } else {
            this.router.navigate(['/home']);
          }

          this.snackBarService.openSnackBar(
            'Login efetuado com sucesso',
            '#0AA174',
            '../../assets/Icons/correct.png'
          );
        },
        (error) => {
          this.isLoading = false;

          if (error.status === 503) {
            this.snackBarService.openSnackBar(
              'Estamos em atualização, prometemos ser breves. Lamentamos qualquer inconveniente',
              '#F97066',
              '../../assets/Icons/ErrorIcon.png'
            );
          } else {
            this.snackBarService.openSnackBar(
              'Erro ao efetuar o login.',
              '#F97066',
              '../../assets/Icons/ErrorIcon.png'
            );
          }
        }
      )
    );
  }



  ngOnDestroy(): void {
    this.subscription.forEach((sub) => sub.unsubscribe());
  }
}
